<script setup lang="ts">
import {
  type Component,
  inject,
  markRaw,
  onMounted,
  onUnmounted,
  provide,
  reactive,
  useId,
} from 'vue';
import { navBarSideKey, registerNavBarItemKey } from './internal';
import type { NavBarNode, RegisterFn } from './types';

const props = withDefaults(
  defineProps<{
    text: string;
    icon?: Component;
    iconOnly?: boolean;
    disabled?: boolean;
    badge?: string | number | null;
    /** Link target — see `NavBarNode.href`. */
    href?: string;
    /** Own popover content instead of a menu — see `NavBarNode.panel`. */
    panel?: Component;
    /** Identity for `NavBar`'s `reorderable` mode — see `NavBarNode.itemKey`. */
    itemKey?: string;
  }>(),
  {
    icon: undefined,
    iconOnly: false,
    disabled: false,
    badge: null,
    href: undefined,
    panel: undefined,
    itemKey: undefined,
  }
);

const emit = defineEmits<{
  action: [event: PointerEvent | KeyboardEvent];
}>();

const id = useId();
const side = inject(navBarSideKey, 'left');
const registerWithParent = inject(registerNavBarItemKey, null);

if (!registerWithParent && import.meta.env.DEV) {
  throw new Error('NavBarItem must be used inside <NavBar>.');
}

// `reactive` so children mutations propagate to renderers.
// Getter properties forward prop changes automatically without watchers.
const node = reactive({
  id,
  get text() {
    return props.text;
  },
  get icon() {
    // `markRaw` stops the reactive `node` proxy from re-wrapping the icon
    // component definition (Vue warns when a component is made reactive).
    return props.icon ? markRaw(props.icon) : undefined;
  },
  get iconOnly() {
    return props.iconOnly;
  },
  get disabled() {
    return props.disabled;
  },
  get badge() {
    return props.badge;
  },
  get href() {
    return props.href;
  },
  get panel() {
    // `markRaw` like `icon`: the panel is a component definition.
    return props.panel ? markRaw(props.panel) : undefined;
  },
  get itemKey() {
    return props.itemKey;
  },
  action: (e: PointerEvent | KeyboardEvent) => emit('action', e),
  side,
  parent: null as NavBarNode | null,
  children: [] as NavBarNode[],
}) satisfies NavBarNode;

let unregister: (() => void) | null = null;
onMounted(() => {
  unregister = registerWithParent?.(node) ?? null;
});
onUnmounted(() => {
  unregister?.();
});

// Expose a register fn for nested NavBarItems.
const registerChild: RegisterFn = (child) => {
  if (import.meta.env.DEV && child.panel) {
    console.warn(
      `NavBarItem "${child.text}": \`panel\` works on root entries only and is ignored here.`
    );
  }
  child.parent = node;
  node.children.push(child);
  return () => {
    const idx = node.children.indexOf(child);
    if (idx >= 0) node.children.splice(idx, 1);
  };
};
provide(registerNavBarItemKey, registerChild);
</script>

<template>
  <!--
    Renderless: no visible DOM (the NavBar renders the entry itself, from the
    node). The default slot is rendered so nested NavBarItem children mount and
    register themselves. The empty marker is the entry's place in the slot:
    when the host moves this component (a keyed `v-for` reordered) Vue moves its
    DOM without a remount, so registration order says nothing about the current
    order — NavBar reads the markers' document order instead (`syncOrder`).
    A parent's marker precedes its children's.
  -->
  <span hidden :data-navbar-order="id" />
  <slot />
</template>
